import get from 'lodash/get'
import isNil from 'lodash/isNil'
import moment from 'moment'
import uuid from 'uuid'
import * as App from '../../../app'
import * as Sortable from '../../../components/sortable'
import * as Request from '../../../request'
import * as CommunityConfigConstants from '../../community-config/constants'
import * as CommunityConfigTypes from '../../community-config/types'
import * as CommunityConfigUtils from '../../community-config/utils'
import * as constants from './constants'
import * as Types from './types'

type State = CommunityConfigTypes.CommunityConfigs
type GridAction = Types.GridUpdateFieldAction &
  Types.GridEditAction &
  Types.DeleteRequestAction &
  Types.AddConfigSuccessAction &
  Request.RequestAction &
  Request.SuccessAction &
  Sortable.SortAction

const normalize = (configRow: CommunityConfigTypes.ConfigRow): CommunityConfigTypes.ConfigRow => {
  return {
    ...configRow,
    created_datetime: moment(configRow.created),
    isSpecific: configRow.source === CommunityConfigConstants.SPECIFIC_CONFIG_SOURCE,
    updated_datetime: moment(configRow.updated),
  }
}

export const dataReducer = (state: State, action: GridAction) => {
  switch (action.type) {
    case Sortable.sortTableConstant(CommunityConfigConstants.FETCH_COMMUNITY_CONFIG):
      return Sortable.sortState(state, action.options.value)
    case Request.successConstant(CommunityConfigConstants.FETCH_COMMUNITY_CONFIG):
      const configs = CommunityConfigUtils.configsFromResponse(action.payload.response)
      return Sortable.sortState(configs.map((configRow: CommunityConfigTypes.ConfigRow) => ({
        data: {
          ...normalize(configRow),
        },
        editing: {
          initialValue: {},
          isEditing: false,
          isSyntheticRow: false,
          isUpdating: false,
        },
      })), [{field: 'data.key', dir: 'asc'}])
    case Request.errorConstant(CommunityConfigConstants.FETCH_COMMUNITY_CONFIG):
    case Request.requestConstant(CommunityConfigConstants.FETCH_COMMUNITY_CONFIG):
    case App.SELECT_COMMUNITY:
      return []
    default:
      return state || []
  }
}

interface IndexedEditableConfigRow extends CommunityConfigTypes.EditableConfigRow {
  rowIndex: number
}

const rowIndexById = (state: State, id: string): number => {
  return state.map((x: CommunityConfigTypes.EditableConfigRow, rowIndex: number) => ({...x, rowIndex}))
              .filter((x: IndexedEditableConfigRow) => x.data.id === id)
              .map((x: IndexedEditableConfigRow) => x.rowIndex)[0]
}

export const editReducer = (state: State, action: GridAction) => {
  const configId = action.configId ||
                   get(action, 'options.payload.configId') ||
                   get(action.requestAction, 'options.payload.configId')
  const rowIndex = rowIndexById(state, configId)

  if (isNil(rowIndex) && action.type !== constants.ADD_ROW) {
    return state
  }
  switch (action.type) {
    case constants.ADD_ROW:
      return [
        {
          data: {
            created: moment.utc().format('ddd, DD MMM YYYY HH:mm:ss z'),
            id: uuid(),
            key: '',
            partner_code: '',
            source: 'specific',
            updated: moment.utc().format('ddd, DD MMM YYYY HH:mm:ss z'),
            value: '',
          },
          editing: {
            ...{
              isEditing: true,
              isSyntheticRow: true,
            },
          },
        },
        ...state,
      ]
    case constants.ENTER_EDIT:
      return [
        ...state.slice(0, rowIndex),
        {
          ...state[rowIndex],
          editing: {
            ...state[rowIndex].editing,
            initialValue: state[rowIndex].data.value,
            isEditing: true,
          },
        },
        ...state.slice(rowIndex + 1),
      ]
    case Request.errorConstant(CommunityConfigConstants.ADD_CONFIG):
    case Request.errorConstant(CommunityConfigConstants.UPDATE_CONFIG):
    case Request.errorConstant(CommunityConfigConstants.DELETE_CONFIG):
      return [
        ...state.slice(0, rowIndex),
        {
          ...state[rowIndex],
          editing: {
            ...state[rowIndex].editing,
            isUpdating: false,
          },
        },
        ...state.slice(rowIndex + 1),
      ]
    case constants.CANCEL_EDIT:
      const item = state[rowIndex]
      if (item.editing.isSyntheticRow) {
        return [
          ...state.slice(0, rowIndex),
          ...state.slice(rowIndex + 1),
        ]
      } else {
        return [
          ...state.slice(0, rowIndex),
          {
            ...state[rowIndex],
            data: {
              ...state[rowIndex].data,
              value: state[rowIndex].editing.initialValue,
            },
            editing: {
              ...state[rowIndex].editing,
              initialValue: {},
              isEditing: false,
              isUpdating: false,
            },
          },
          ...state.slice(rowIndex + 1),
        ]
      }
    case constants.TOGGLE_CONFIRM_DELETE:
      return [
        ...state.slice(0, rowIndex),
        {
          ...state[rowIndex],
          editing: {
            ...state[rowIndex].editing,
            confirmDelete: !state[rowIndex].editing.confirmDelete,
          },
        },
        ...state.slice(rowIndex + 1),
      ]
    case constants.CANCEL_DELETE:
      return [
        ...state.slice(0, rowIndex),
        {
          ...state[rowIndex],
          editing: {
            confirmDelete: false,
          },
        },
        ...state.slice(rowIndex + 1),
      ]
    case constants.UPDATE_FIELD:
      return [
        ...state.slice(0, rowIndex),
        {
          ...state[rowIndex],
          data: {
            ...state[rowIndex].data,
            [action.field]: action.value,
          },
        },
        ...state.slice(rowIndex + 1),
      ]
    case Request.requestConstant(CommunityConfigConstants.UPDATE_CONFIG):
    case Request.requestConstant(CommunityConfigConstants.ADD_CONFIG):
    case Request.requestConstant(CommunityConfigConstants.DELETE_CONFIG):
      return [
        ...state.slice(0, rowIndex),
        {
          ...state[rowIndex],
          editing: {
            ...state[rowIndex].editing,
            isUpdating: true,
          },
        },
        ...state.slice(rowIndex + 1),
      ]
    case Request.successConstant(CommunityConfigConstants.UPDATE_CONFIG):
    case Request.successConstant(CommunityConfigConstants.ADD_CONFIG):
      return [
        ...state.slice(0, rowIndex),
        {
          ...state[rowIndex],
          data: {
            ...normalize(action.payload.response),
          },
          editing: {
            initialValue: {},
            isEditing: false,
            isSyntheticRow: false,
            isUpdating: false,
          },
        },
        ...state.slice(rowIndex + 1),
      ]
    case Request.successConstant(CommunityConfigConstants.DELETE_CONFIG):
      return [
        ...state.slice(0, rowIndex),
        ...state.slice(rowIndex + 1),
      ]
    default:
      return state || []
  }
}



// WEBPACK FOOTER //
// ./src/pages/community-config/community-grid/reducer.ts